import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Reusable URL <-> state helpers for list pages (search, filters, sort,
 * pagination). Keeps a page's view shareable/bookmarkable and restored when you
 * navigate to a detail page and come back.
 *
 * Usage in a list page:
 *
 *   const [searchParams] = useSearchParams();
 *   const [searchTerm, setSearchTerm] = useState(() => readParam(searchParams, 'q', ''));
 *   const [currentPage, setCurrentPage] = useState(() => readParam(searchParams, 'page', 1, 'number'));
 *   ...
 *   useSyncUrlParams(
 *     { q: searchTerm.trim(), sort: sortBy, order: sortOrder, page: currentPage, size: itemsPerPage },
 *     { q: '', sort: 'ID', order: 'desc', page: 1, size: 20 },
 *   );
 *
 * The page keeps its own state, handlers, and data-loading; these helpers only
 * (1) seed the initial values from the URL and (2) mirror them back into it.
 */

/**
 * Read a single query param for a useState initializer.
 * @param {URLSearchParams} searchParams
 * @param {string} key
 * @param {*} defaultValue returned when the param is absent/empty/invalid
 * @param {'string'|'number'|'boolean'} type
 */
export function readParam(searchParams, key, defaultValue, type = 'string') {
  const raw = searchParams.get(key);
  if (raw == null || raw === '') return defaultValue;
  if (type === 'number') {
    const n = Number(raw);
    return Number.isFinite(n) ? n : defaultValue;
  }
  if (type === 'boolean') return raw === 'true';
  return raw;
}

/**
 * Mirror a flat `{ paramKey: value }` map into the URL query string.
 *
 * - Omits any value equal to its default (keeps URLs short and clean).
 * - Omits empty ('') / null / undefined values.
 * - Writes ALL params in a single effect, so they can never clobber each other
 *   the way multiple independent `setSearchParams` calls would.
 * - Uses `replace` so filter/paging changes don't flood the history stack.
 *
 * @param {Record<string, string|number>} params current state, keyed by URL param
 * @param {Record<string, string|number>} defaults values that should NOT appear in the URL
 */
export function useSyncUrlParams(params, defaults = {}) {
  const [, setSearchParams] = useSearchParams();
  // Stable dependency: only re-sync when a value actually changes.
  const serialized = JSON.stringify(params);

  useEffect(() => {
    const next = {};
    Object.entries(params).forEach(([key, value]) => {
      if (value === '' || value == null) return;
      if (String(value) === String(defaults[key])) return;
      next[key] = String(value);
    });
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serialized]);
}
